import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRedemptions } from '@services/redemption.service';
import QueryTransaction, { ITransactions } from '@interfaces/transaction';
import { groupRedemptionsByDate, markNewRedemptionAsOld, ungroupRedemptionsByDate } from '@helpers/redemption.helper';
import { IRedemption } from '@interfaces/redemption';

export const fetchRedemptions = createAsyncThunk('redemption/fetchRedemptions', async (query: QueryTransaction) => {
	return await getRedemptions(query);
});

export const refreshRedemptions = createAsyncThunk('redemption/refreshRedemptions', async (query: QueryTransaction) => {
	return await getRedemptions(query);
});

export interface RedemptionsState {
	transactions: ITransactions<IRedemption>[];
	amount: number;
	numOfTransaction: number;
	isLoading: boolean;
	isRefreshing: boolean;
	metadata: {
		page: number;
		pages: number;
		total: number;
	};
}

const initialState: RedemptionsState = {
	transactions: [],
	amount: 0,
	numOfTransaction: 0,
	isLoading: false,
	isRefreshing: false,
	metadata: {
		page: 0,
		pages: 0,
		total: 0,
	},
};

const redemptionSlice = createSlice({
	name: 'redemption',
	initialState,
	reducers: {
		markRedemptionAsOld: (state, action: PayloadAction<string>) => {
			state.transactions = state.transactions.map((transactionGroup) => {
				transactionGroup.transactions = transactionGroup.transactions.map((transaction) =>
					transaction.redemptionId === action.payload ? { ...transaction, isNew: false } : transaction,
				);
				return transactionGroup;
			});
		},
		setRefreshing: (state, action: PayloadAction<boolean>) => {
			state.isRefreshing = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(fetchRedemptions.pending, (state: RedemptionsState) => {
			if (state.transactions.length === 0) {
				state.isLoading = true;
			}
		});
		builder.addCase(fetchRedemptions.fulfilled, (state: RedemptionsState, action) => {
			state.isLoading = false;
			const data = action.payload['data'];
			state.numOfTransaction = data?.length ?? 0;

			const existingRedemptions = ungroupRedemptionsByDate(state.transactions);
			const newRedemptions = markNewRedemptionAsOld(data, existingRedemptions);

			const { transactionsGroup } = groupRedemptionsByDate(newRedemptions);
			state.transactions = transactionsGroup ?? [];
		});
		builder.addCase(fetchRedemptions.rejected, (state: RedemptionsState) => {
			state.isLoading = false;
		});
	},
});

export const redemptionReducer = redemptionSlice.reducer;
export const { markRedemptionAsOld, setRefreshing } = redemptionSlice.actions;
